import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, tap } from 'rxjs';
import { BrandService } from 'src/lib/services/brand.service';
import { HomeScreenService } from 'src/lib/services/home-screen.service';
import { AuthService } from '../../../lib/auth/auth.service';
import { UserCardApi } from '../../../lib/repository/user-card/user-card.api';
import { IUserCard } from '../../../lib/repository/user-card/user-card.model';
import { IUserInfo } from '../../../lib/repository/user-info/user-info.model';
import { DialogService } from '../../services/dialog.service';
import { UiService } from '../../services/ui.service';
import { BookingPackagegoodsStateItem } from '../../states/booking-packagegoods-list.state-item';

/**
 * 템플릿 레이아웃 컴포넌트
 *
 * 이 컴포넌트는 애플리케이션의 기본 레이아웃을 구성하는 메인 컴포넌트입니다.
 * 헤더, 푸터, 사이드 메뉴 등의 기본 레이아웃 요소들을 포함하고 있으며,
 * 사용자 정보 관리와 브랜드 설정을 담당합니다.
 */
@Component({
  selector: 'trnty-template-layout',
  templateUrl: './template-layout.component.html',
  styleUrls: ['./template-layout.component.scss'],
})
export class TemplateLayoutComponent implements OnInit {
  // 언어 변경 버튼 표시 여부 (홈스크린 설정에서 가져옴)
  canChangeLanguage = this.homeScreenService.homeScreen.languageButtonVisible;

  // 푸터 표시 여부 (기본값: true)
  footerFlg = this.homeScreenService.homeScreen.footerFlg ?? true;

  /**
   * 현재 로그인한 사용자의 정보
   * @type {IUserInfo | null}
   */
  userInfo: IUserInfo | null = null;

  /**
   * 사용자의 포인트 카드 정보
   * @type {IUserCard | null}
   */
  userCard: IUserCard | null = null;

  /**
   * 포인트 카드 정보를 가져오는 Observable
   * @private
   */
  private userCard$: Observable<IUserCard> | null = null;

  constructor(
    private authService: AuthService,
    private brandService: BrandService,
    private homeScreenService: HomeScreenService,
    private uiService: UiService,
    private userCardApi: UserCardApi,
    private bookingPackagegoodsStateItem: BookingPackagegoodsStateItem,
    private router: Router,
    private dialogService: DialogService,
  ) {
    // 패키지 상품 목록 로드
    this.bookingPackagegoodsStateItem.loadPackagegoods();
  }

  /**
   * 컴포넌트 초기화
   * - 문서 제목 및 아이콘 설정
   * - 사용자 정보 설정
   * - SEO 메타 태그 설정
   */
  ngOnInit(): void {
    // 홈페이지 아이콘 및 제목 업데이트
    const pageTitle =
      this.homeScreenService.homeScreen.homepageTitle ??
      this.brandService.brand.brandNm ??
      'K-Beauty';
    const favicon =
      this.homeScreenService.homeScreen.homepageFaviconImageFileData?.path ??
      this.homeScreenService.homeScreen.homepageFaviconImageUrl ??
      this.brandService.brand.logoImgUrl ??
      '';

    // 문서 제목 및 아이콘 업데이트
    this.uiService.updateDocumentTitle(pageTitle);
    this.uiService.updateDocumentIcon(favicon);

    this.setUserInfo();
  }

  /**
   * 사용자 정보 갱신
   * 포인트 카드 정보를 초기화하고 계정 정보를 새로고침합니다.
   */
  onRefreshInfoClick(): void {
    this.userCard$ = null;
    this.authService.refreshAccount$().subscribe();
  }

  /**
   * 무료 상담 클릭 이벤트 처리
   */
  onConsultationClick(): void {
    if (this.authService.account) {
      this.router.navigate(['requests', 'packagegoods']);
    } else {
      this.dialogService
        .confirm(
          '상담 받기를 위해서는 로그인이 필요합니다.<br />로그인하시겠습니까?',
        )
        .subscribe((result) => {
          if (result) {
            this.router.navigate(['requests'], {
              queryParams: {
                redirectTo: '/requests/packagegoods',
              },
            });
          }
        });
    }
  }

  /**
   * 메시지 클릭 이벤트 처리
   */
  onMessageClick(): void {
    if (this.authService.account) {
      this.router.navigate(['message']);
    } else {
      this.dialogService
        .confirm(
          '메시지를 이용하기 위해서는 로그인이 필요합니다.<br />로그인하시겠습니까?',
        )
        .subscribe((result) => {
          if (result) {
            this.router.navigate(['requests']);
          }
        });
    }
  }

  /**
   * 사용자 정보 설정
   * @private
   * - 현재 로그인한 사용자 정보 설정
   * - 포인트 사용 가능한 브랜드인 경우 포인트 카드 정보 조회
   */
  private setUserInfo(): void {
    if (this.authService.account) {
      const { userInfo } = this.authService.account;
      this.userInfo = userInfo;

      // 포인트 사용 가능 브랜드면 조회
      if (this.brandService.isAvail('userInfo_point')) {
        this.setUserCard(userInfo);
      }
    }

    this.authService.loginSubject$
      .asObservable()
      .pipe(
        map((account) => {
          return account?.userInfo;
        }),
        tap((userInfo) => {
          this.userInfo = userInfo;

          // 포인트 사용 가능 브랜드면 조회
          if (this.brandService.isAvail('userInfo_point')) {
            this.setUserCard(userInfo);
          }
        }),
      )
      .subscribe();
  }

  /**
   * 포인트 카드 정보 설정
   * @private
   * @param userInfo - 사용자 정보
   * - 포인트 사용 가능한 브랜드에서 사용자의 포인트 카드 정보를 조회하여 설정
   */
  private setUserCard(userInfo: IUserInfo | null): void {
    if (this.userCard$) {
      return;
    }

    if (userInfo?.userCard?.id) {
      this.userCardApi
        .findItem(userInfo?.userCard?.id)
        .pipe(
          tap((userCard) => {
            this.userCard = userCard;
          }),
        )
        .subscribe();
    }
  }
}
